import type { ApiEnvelope } from '@lunacast/api-contracts';
import {
  adminContract,
  ApiError,
  createBackendApiClient,
  requirePayload,
} from '@lunacast/api-contracts';
import { createAuthClient } from '@lunacast/web-auth-client';
import type { AdminUserRole } from '@lunacast/api-contracts/admin';
import type { AdminAccount } from '@/types/adminAccount';
import type {
  IngestBroadcastFilter,
  IngestBroadcastItem,
  IngestBroadcastsMeta,
  IngestDonation,
  IngestFleetStatus,
  IngestSuspendedTarget,
} from '@/types/ingest';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

export { ApiError };

type ProfileUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  isTempPassword: boolean;
};

type MemberLivePlatform = {
  platform: string;
  nativeId: string;
};

type MemberLinkedPlatform = {
  platform: string;
  nativeId: string | null;
  displayName: string | null;
  hasApiCapability: boolean;
  collecting: boolean;
};

type MemberUser = {
  id: string;
  email: string;
  name: string;
  role: string;
  tier: string;
  subscriptionStatus: string;
  livePlatforms: MemberLivePlatform[];
  isTempPassword: boolean;
  createdAt: string;
  updatedAt: string;
};

type MemberDetailUser = MemberUser & {
  linkedPlatforms: MemberLinkedPlatform[];
  cancelAtPeriodEnd: boolean | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
};

type AdminAccountUser = AdminAccount;

type OAuthProviderAdminRecord = {
  provider: string;
  enabled: boolean;
  clientId: string | null;
  hasSecret: boolean;
  rotatedAt: string | null;
  updatedAt: string;
  redirectUri: string;
};

type TermDocumentAdminRecord = {
  id: string;
  type: string;
  version: number;
  title: string;
  summary: string;
  body: string;
  status: 'draft' | 'published';
  requiresReconsent: boolean;
  publishedAt: string | null;
  effectiveAt: string | null;
  createdAt: string;
  updatedAt: string;
};

type CommunicationEventId =
  | 'auth.signup.verification'
  | 'auth.login.verification'
  | 'auth.admin_mfa.verification'
  | 'auth.email_bind.verification';

type CommunicationFieldMeta = {
  name: string;
  type: 'string' | 'number';
  label: string;
  widget: 'input' | 'textarea' | 'number';
  optional: boolean;
};

type CommunicationEventStats = {
  sentToday: number;
  sent7d: number;
  sent30d: number;
  blocked30d: number;
  failed30d: number;
  testSent30d: number;
  volumeWarning: 'high' | 'low' | null;
  observationDayCount: number;
};

type CommunicationEventListItem = {
  id: CommunicationEventId;
  channel: 'email';
  title: string;
  description: string;
  enabled: boolean;
  controlHealthy: boolean;
  stats: CommunicationEventStats;
};

type CommunicationEventDetail = CommunicationEventListItem & {
  fields: CommunicationFieldMeta[];
  defaultValues: Record<string, unknown>;
  updatedAt: string | null;
  updatedByAdminId: string | null;
};

type TokenRefreshPayload = {
  accessToken: string;
  accessTokenExpiresAt: string;
  familyId: string;
};

type LoginTiming = {
  expiresAt: string;
  resendAvailableAt: string;
};

type LoginVerifiedPayload = TokenRefreshPayload & ProfileUser;

let logoutRedirectScheduled = false;
let sessionExpiredHandler: (() => void) | null = null;

/**
 * Root에서 React Router `navigate`를 등록한다.
 * 세션 만료 시 `window.location.assign` full reload 대신 client navigate로 /login 이동.
 */
export function registerSessionExpiredHandler(handler: (() => void) | null): void {
  sessionExpiredHandler = handler;
}

const authClient = createAuthClient({
  baseUrl: BASE,
  storageNamespace: 'lunacast-admin',
  onSessionExpired: () => {
    if (logoutRedirectScheduled || typeof window === 'undefined') {
      return;
    }
    logoutRedirectScheduled = true;
    if (window.location.pathname.startsWith('/login')) {
      return;
    }
    if (sessionExpiredHandler) {
      sessionExpiredHandler();
      return;
    }
    // Bridge 마운트 전(극초기)만 full navigation fallback
    window.location.assign('/login');
  },
});

const apiClient = createBackendApiClient({
  contract: adminContract,
  authClient,
});

function unwrapPayload<T>(result: { status: number; body: unknown }): T {
  if (result.status >= 400) {
    throw new ApiError(result.status, result.body);
  }
  return requirePayload(result.body as ApiEnvelope<T>);
}

export function clearAccessToken(): void {
  authClient.clearAccessToken();
}

async function withDpopBinding<T extends Record<string, unknown>>(
  htu: string,
  body: T,
  method: 'POST' | 'PATCH' = 'POST',
): Promise<T & { dpopProof?: string }> {
  const dpopProof = await authClient.createDpopProof({ htm: method, htu });
  return dpopProof ? { ...body, dpopProof } : body;
}

function storeSession(payload: TokenRefreshPayload): void {
  logoutRedirectScheduled = false;
  authClient.setAccessToken(payload.accessToken, payload.accessTokenExpiresAt, payload.familyId);
}

export const api = {
  refreshSession: async () => {
    const ok = await authClient.refreshSession();
    if (!ok) {
      throw new ApiError(401);
    }
  },

  logout: async () => {
    try {
      const result = await apiClient.auth.logout({ body: null });
      if (result.status >= 400) {
        throw new ApiError(result.status, result.body);
      }
    } finally {
      clearAccessToken();
    }
  },

  /** 1단계: 비밀번호 검증 후 OTP 발송 — 토큰 없음 */
  login: async (email: string, password: string) => {
    const payload = unwrapPayload<LoginTiming>(
      await apiClient.auth.login({ body: { email, password } }),
    );
    return { payload };
  },

  /** 2단계: OTP 검증 후 세션 발급 */
  verifyOtp: async (email: string, code: string) => {
    const payload = unwrapPayload<LoginVerifiedPayload>(
      await apiClient.auth.verifyOtp({
        body: await withDpopBinding('/auth/login/verify-otp', { email, code }),
      }),
    );
    storeSession(payload);
    return { payload };
  },

  resetTempPassword: async (currentPassword: string, newPassword: string) => {
    const payload = unwrapPayload<TokenRefreshPayload>(
      await apiClient.auth.resetTempPassword({
        body: await withDpopBinding('/auth/reset-temp', { currentPassword, newPassword }, 'PATCH'),
      }),
    );
    storeSession(payload);
    return { payload };
  },

  getProfile: async () => unwrapPayload<ProfileUser>(await apiClient.users.getProfile()),

  changePassword: async (currentPassword: string, newPassword: string) => {
    const payload = unwrapPayload<TokenRefreshPayload>(
      await apiClient.users.changePassword({
        body: await withDpopBinding(
          '/user/change-password',
          { currentPassword, newPassword },
          'PATCH',
        ),
      }),
    );
    storeSession(payload);
    return { payload };
  },

  getMembers: async () =>
    unwrapPayload<MemberUser[]>(await apiClient.members.getMembers()),

  getMember: async (id: string) =>
    unwrapPayload<MemberDetailUser>(await apiClient.members.getMember({ params: { id } })),

  getAdminUsers: async () =>
    unwrapPayload<AdminAccountUser[]>(await apiClient.adminUsers.list()),

  /** 직원 역할을 `admin`과 `super_admin` 사이에서 바꾼다. */
  updateAdminUserRole: async (id: string, role: AdminUserRole) =>
    unwrapPayload<AdminAccountUser>(
      await apiClient.adminUsers.updateRole({ params: { id }, body: { role } }),
    ),

  /** 직원 계정을 정지한다. */
  deactivateAdminUser: async (id: string) =>
    unwrapPayload<AdminAccountUser>(
      await apiClient.adminUsers.deactivate({ params: { id }, body: {} }),
    ),

  /** 정지된 직원 계정을 다시 활성화한다. 폐기된 세션은 복원하지 않는다. */
  reactivateAdminUser: async (id: string) =>
    unwrapPayload<AdminAccountUser>(
      await apiClient.adminUsers.reactivate({ params: { id }, body: {} }),
    ),

  updateMemberRole: async (id: string, role: string) =>
    unwrapPayload<MemberUser>(
      await apiClient.members.updateMemberRole({ params: { id }, body: { role } }),
    ),

  deleteMember: async (id: string) => {
    const result = await apiClient.members.deleteMember({ params: { id } });
    if (result.status >= 400) {
      throw new ApiError(result.status, result.body);
    }
  },

  resetMemberPassword: async (id: string) =>
    unwrapPayload<MemberUser>(
      await apiClient.members.resetMemberPassword({ params: { id }, body: undefined }),
    ),

  getOAuthProviders: async () =>
    unwrapPayload<OAuthProviderAdminRecord[]>(await apiClient.oauthProviders.list()),

  updateOAuthProvider: async (
    provider: string,
    input: { enabled?: boolean; clientId?: string | null },
  ) =>
    unwrapPayload<OAuthProviderAdminRecord>(
      await apiClient.oauthProviders.update({ params: { provider }, body: input }),
    ),

  rotateOAuthProviderSecret: async (provider: string, clientSecret: string) =>
    unwrapPayload<OAuthProviderAdminRecord>(
      await apiClient.oauthProviders.rotateSecret({
        params: { provider },
        body: { clientSecret },
      }),
    ),

  getTerms: async (filters?: { type?: string; status?: string }) =>
    unwrapPayload<TermDocumentAdminRecord[]>(
      await apiClient.terms.list({ query: filters ?? {} }),
    ),

  createTermDraft: async (input: {
    type: string;
    title: string;
    summary: string;
    body: string;
    effectiveAt?: string | null;
    requiresReconsent?: boolean;
  }) =>
    unwrapPayload<TermDocumentAdminRecord>(await apiClient.terms.create({ body: input })),

  updateTermDraft: async (
    id: string,
    input: {
      title?: string;
      summary?: string;
      body?: string;
      effectiveAt?: string | null;
      requiresReconsent?: boolean;
    },
  ) =>
    unwrapPayload<TermDocumentAdminRecord>(
      await apiClient.terms.update({ params: { id }, body: input }),
    ),

  publishTerm: async (id: string) =>
    unwrapPayload<TermDocumentAdminRecord>(
      await apiClient.terms.publish({ params: { id }, body: undefined }),
    ),

  deleteTermDraft: async (id: string) => {
    const result = await apiClient.terms.remove({ params: { id } });
    if (result.status >= 400) {
      throw new ApiError(result.status, result.body);
    }
  },

  getIngestBroadcasts: async (query: {
    filter?: IngestBroadcastFilter;
    page?: number;
    pageSize?: number;
  }) =>
    unwrapPayload<{ items: IngestBroadcastItem[]; meta: IngestBroadcastsMeta }>(
      await apiClient.ingest.listBroadcasts({ query }),
    ),

  getIngestThroughput: async (bjIds: string[]) =>
    unwrapPayload<{
      throughputTotal: number | null;
      collectorOk: boolean;
      items: Array<{
        bjId: string;
        collecting: boolean;
        throughputEventsPerSec: number | null;
      }>;
    }>(
      await apiClient.ingest.getThroughput({
        query: { bjIds: bjIds.join(',') },
      }),
    ),

  getIngestDonations: async (
    bjId: string,
    query?: { limit?: number; cursor?: number },
  ) =>
    unwrapPayload<{
      items: IngestDonation[];
      nextCursor: number | null;
      collectorOk: boolean;
    }>(await apiClient.ingest.listDonations({ params: { bjId }, query: query ?? {} })),

  getIngestFleetStatus: async () =>
    unwrapPayload<IngestFleetStatus>(await apiClient.ingest.getFleetStatus()),

  getTopDonors: async (query: { period?: 'today' | '7d' | '30d'; limit?: number }) =>
    unwrapPayload<{
      period: 'today' | '7d' | '30d';
      windowStart: string;
      windowEnd: string;
      todayDate: string;
      rollupDates: string[];
      livePartial: boolean;
      items: Array<{
        rank: number;
        provider: string;
        donorPlatformUserId: string;
        donorDisplayName: string;
        unit: string;
        totalQuantity: number;
        eventCount: number;
      }>;
    }>(await apiClient.analysis.listTopDonors({ query })),

  setIngestWorkerQuarantine: async (
    workerId: string,
    body: { quarantine: boolean; reason?: string },
  ) =>
    unwrapPayload<{ workerId: string; quarantine: boolean }>(
      await apiClient.ingest.setWorkerQuarantine({ params: { workerId }, body }),
    ),

  suspendIngestTarget: async (body: { platform: string; nativeId: string; reason?: string }) =>
    unwrapPayload<IngestSuspendedTarget>(await apiClient.ingest.suspendTarget({ body })),

  resumeIngestTarget: async (body: { platform: string; nativeId: string }) =>
    unwrapPayload<{ platform: string; nativeId: string; resumed: boolean }>(
      await apiClient.ingest.resumeTarget({ body }),
    ),

  getCommunicationEvents: async () =>
    unwrapPayload<CommunicationEventListItem[]>(await apiClient.communications.list()),

  getCommunicationEvent: async (eventId: CommunicationEventId) =>
    unwrapPayload<CommunicationEventDetail>(
      await apiClient.communications.get({ params: { eventId } }),
    ),

  previewCommunicationEvent: async (
    eventId: CommunicationEventId,
    data: Record<string, unknown>,
  ) =>
    unwrapPayload<{ subject: string; html: string; text: string }>(
      await apiClient.communications.preview({ params: { eventId }, body: { data } }),
    ),

  testSendCommunicationEvent: async (
    eventId: CommunicationEventId,
    to: string,
    data: Record<string, unknown>,
  ) =>
    unwrapPayload<{ subject: string; provider: 'console' | 'acs' }>(
      await apiClient.communications.testSend({
        params: { eventId },
        body: { to, data },
      }),
    ),

  updateCommunicationEventControl: async (
    eventId: CommunicationEventId,
    enabled: boolean,
  ) =>
    unwrapPayload<{
      id: CommunicationEventId;
      enabled: boolean;
      controlHealthy: boolean;
      updatedAt: string | null;
      updatedByAdminId: string | null;
    }>(
      await apiClient.communications.updateControl({
        params: { eventId },
        body: { enabled },
      }),
    ),

  /** admin 허브 Client Access URL 발급 — 이번 범위는 연결 인프라 스모크 검증까지만이다. */
  negotiateRealtime: async () =>
    unwrapPayload<{ url: string }>(await apiClient.realtime.negotiate()),

  testPublishRealtime: async (message?: string) => {
    const result = await apiClient.realtime.testPublish({ body: { message } });
    if (result.status >= 400) {
      throw new ApiError(result.status, result.body);
    }
  },
};

export type {
  MemberUser,
  MemberDetailUser,
  MemberLivePlatform,
  MemberLinkedPlatform,
  OAuthProviderAdminRecord,
  TermDocumentAdminRecord,
  CommunicationEventId,
  CommunicationFieldMeta,
  CommunicationEventStats,
  CommunicationEventListItem,
  CommunicationEventDetail,
};
